import { type InputProps as MuiInputProps, TextField } from '@mui/material';
import { Field, FieldProps, useFormikContext } from 'formik';
import { camelCase, get, isFinite } from 'lodash';
import React, { ChangeEvent, useCallback, useMemo } from 'react';

import { InputComponent } from '..';
import { normalizeSize } from '../helpers';

export interface InputTextProps extends InputComponent {
  type?: HTMLInputElement['type'];
  min?: number | string;
  max?: number | string;
  step?: number;
  onChange?: (name: string, value: number | string) => void;
  InputProps?: Partial<MuiInputProps>;
}

export function InputText({
  label,
  disabled,
  hidden,
  required,
  type = 'text',
  className,
  hint,
  nameOverride,
  min = undefined,
  max = undefined,
  placeholder,
  size: propSize = 'medium',
  onChange = undefined,
  step = 1,
  showLabel = !hidden,
  readOnly = false,
  InputProps = undefined,
}: InputTextProps) {
  const { errors, handleChange } = useFormikContext<Record<string, string>>();

  const validate = useCallback(
    (value: string | number | undefined) => {
      if (type === 'number') {
        if (required && !isFinite(value)) {
          return `${label} is required.`;
        }
      } else if (required && (!value || value === undefined)) {
        return `${label} is required.`;
      }
      return undefined;
    },
    [label, required, type],
  );

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const inputPlaceholder = useMemo(
    () => placeholder ?? label?.replace(/[?:]/g, ''),
    [placeholder, label],
  );

  const size = normalizeSize(propSize);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(name, e.target.value);
      handleChange(e);
    },
    [name, onChange, handleChange],
  );

  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLInputElement>) => {
      if (type === 'number') {
        e.currentTarget?.blur();
      }
    },
    [type],
  );

  const error = useMemo(() => get(errors, name), [errors, name]);

  return (
    <Field name={name} validate={validate} isInvalid={!!error}>
      {({ field }: FieldProps) => (
        <TextField
          onBlur={field.onBlur}
          name={name}
          fullWidth
          variant="outlined"
          label={showLabel && label}
          className={className}
          disabled={disabled}
          hidden={hidden}
          required={required && !readOnly}
          placeholder={inputPlaceholder}
          slotProps={{
            inputLabel: { shrink: !!(showLabel && label) },
            input: {
              ...InputProps,
              inputProps: {
                min,
                max,
                step,
                readOnly,
                ...(type === 'number' && { onWheel: handleWheel }),
              } as React.HTMLAttributes<HTMLInputElement>,
            },
          }}
          type={type}
          size={size === 'large' ? 'medium' : size}
          onChange={handleInputChange}
          value={field.value ?? ''}
          error={!!error}
          helperText={error || hint}
        />
      )}
    </Field>
  );
}
